/**
 * Love Scorer — Sprint 16b Dual Confidence Model
 *
 * Provides dual confidence scoring (LLM + Bayesian blend) for love entities.
 * The LLM provides raw confidence; Bayesian updates converge over multiple runs;
 * a sigmoid blend weights LLM more early, Bayesian more over time.
 */
/**
 * Bayesian update with decreasing learning rate.
 * Early runs: fast learning (0.3). After 5+ runs: slow adjustment (0.1).
 */
export function updateBayesianConfidence(current, newLlmConfidence, confirmationCount) {
    const learningRate = Math.max(0.1, 0.3 - confirmationCount * 0.04);
    return Math.min(1, current + (1 - current) * newLlmConfidence * learningRate);
}
/**
 * Stability-informed temporal decay.
 * Transient (stability=0): 3%/day
 * Core (stability=0.95): 0.15%/day
 */
export function decayBayesianConfidence(current, daysSinceLastConfirmation, stability) {
    const decayRate = 0.03 * (1 - stability);
    return Math.max(0, current * Math.pow(1 - decayRate, daysSinceLastConfirmation));
}
/**
 * Smooth sigmoid blend between LLM and Bayesian confidence.
 * count=0: 0.70 LLM weight → count→∞: 0.40 LLM weight
 */
export function blendConfidence(llmConfidence, bayesianConfidence, confirmationCount) {
    const llmWeight = 0.4 + 0.3 / (1 + confirmationCount);
    return llmWeight * llmConfidence + (1 - llmWeight) * bayesianConfidence;
}
/**
 * Initialize dual confidence from first LLM detection.
 */
export function initDualConfidence(llmConfidence) {
    return {
        llmConfidence,
        bayesianConfidence: llmConfidence, // First detection: bayesian = llm
        finalConfidence: llmConfidence,
        confirmationCount: 0,
    };
}
/**
 * Update dual confidence when entity is confirmed in a detection run.
 */
export function confirmDualConfidence(dual, newLlmConfidence) {
    const confirmationCount = dual.confirmationCount + 1;
    const bayesianConfidence = updateBayesianConfidence(dual.bayesianConfidence, newLlmConfidence, dual.confirmationCount);
    const finalConfidence = blendConfidence(newLlmConfidence, bayesianConfidence, confirmationCount);
    return {
        llmConfidence: newLlmConfidence,
        bayesianConfidence,
        finalConfidence,
        confirmationCount,
    };
}
/**
 * Apply temporal decay when entity is NOT confirmed in a run.
 */
export function decayDualConfidence(dual, daysSinceLastConfirmation, stability) {
    const bayesianConfidence = decayBayesianConfidence(dual.bayesianConfidence, daysSinceLastConfirmation, stability);
    const finalConfidence = blendConfidence(dual.llmConfidence, bayesianConfidence, dual.confirmationCount);
    return {
        ...dual,
        bayesianConfidence,
        finalConfidence,
    };
}
/**
 * Sprint 17c: Apply feedback history to dual confidence.
 * Mission lifecycle transitions feed back into entity confidence.
 *
 * - mission_accepted: +0.05
 * - mission_rejected: -0.10 (rejections are stronger signals)
 * - confirm: +0.15
 * - dispute: -0.20
 * - tap: +0.02 (mild positive)
 *
 * Total adjustment capped at ±0.3.
 */
export function applyFeedbackHistory(dual, feedback) {
    if (feedback.length === 0)
        return dual;
    const adjustmentMap = {
        mission_accepted: 0.05,
        mission_rejected: -0.10,
        confirm: 0.15,
        dispute: -0.20,
        tap: 0.02,
    };
    let totalAdjustment = 0;
    for (const f of feedback) {
        totalAdjustment += adjustmentMap[f.type] ?? 0;
    }
    // Cap at ±0.3
    totalAdjustment = Math.max(-0.3, Math.min(0.3, totalAdjustment));
    if (totalAdjustment === 0)
        return dual;
    const adjustedBayesian = Math.max(0, Math.min(1, dual.bayesianConfidence + totalAdjustment));
    const finalConfidence = blendConfidence(dual.llmConfidence, adjustedBayesian, dual.confirmationCount);
    return {
        ...dual,
        bayesianConfidence: adjustedBayesian,
        finalConfidence,
    };
}
/**
 * Apply user confirmation boost (counts as 2 confirmations with max confidence signal).
 */
export function userConfirmBoost(dual) {
    const bayesianConfidence = updateBayesianConfidence(dual.bayesianConfidence, 1.0, // max confidence signal
    dual.confirmationCount);
    const confirmationCount = dual.confirmationCount + 2;
    const finalConfidence = blendConfidence(dual.llmConfidence, bayesianConfidence, confirmationCount);
    return {
        ...dual,
        bayesianConfidence,
        finalConfidence,
        confirmationCount,
    };
}
//# sourceMappingURL=love-scorer.js.map